// Personal settings at /settings (architecture sprint-04 §6.4, S7.1; sprint 7 §7.9 original sections).
// Each field saves on change; no optimistic UI: a control is disabled while its PATCH is in flight and
// re-renders from `me`.
import { CHECKIN_INTERVAL_MINUTES } from '@shared/report-rules';
import { useState, type ReactNode } from 'react';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { SectionTitle } from '@/components/SectionTitle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useMe } from '@/hooks/use-me';
import { useUpdatePreferences } from '@/hooks/use-preferences';
import { useProjects } from '@/hooks/use-projects';
import { useMeSettings } from '@/hooks/use-settings';
import type { MeSetting } from '@/types/settings';
import { notificationPermission, requestNotificationPermission, type PermissionState } from '@/lib/notifications';
import type { Me, PreferencesPatch } from '@/types/me';
import { playTest } from '@/lib/alarm-audio';
import { ALARM_SOUND_LABELS, type AlarmSoundKind } from '@/lib/alarm-sounds';
import { setSoundBlocked } from '@/lib/alarm-ui-state';
import { isShiftProject } from '@/types/projects';
import { browserZone, TimezonePicker } from './TimezonePicker';

export const UNSUPPORTED_NOTE = "This browser doesn't support notifications.";
export const BLOCKED_NOTE =
  "Notifications are blocked for this site. Allow them in your browser's site settings (the lock icon in the address bar), then turn this on again.";
export const TIMEZONE_HELP =
  "Your timezone sets your 'today', week and month boundaries, and how timers split at midnight. Entries you've already logged keep their dates.";

const MINUTES_PER_HOUR = 60;

type Field = keyof PreferencesPatch;

function intervalLabel(minutes: number): string {
  return minutes === MINUTES_PER_HOUR ? 'Every hour' : `Every ${minutes} minutes`;
}

const DEFAULT_OPTION = 'default';
export const NOT_LOGGABLE_DEFAULT_NOTE = 'Not available to you; new time uses No project.';

function settingValueText(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'number') return `${value} min`;
  return String(value);
}

/** Sprint 11 (§9.5): the projects that enforce this preference. The control stays editable (No project and other projects still use it). */
export function EnforcedNote({ setting }: { setting: MeSetting | undefined }): JSX.Element | null {
  if (!setting || setting.enforcedBy.length === 0) return null;
  return (
    <ul className="space-y-0.5 text-xs text-muted-foreground" data-testid={`enforced-${setting.key}`}>
      {setting.enforcedBy.map((e) => (
        <li key={e.projectId}>{`On ${e.projectName}, this is set by the project: ${settingValueText(e.value)}.`}</li>
      ))}
    </ul>
  );
}

const ALARM_SOUNDS: readonly AlarmSoundKind[] = ['klaxon', 'bell', 'siren'];
export const SET_BY_PROJECT = 'Set by the project';

/**
 * Sprint 14 (§8.7): the person's alarm sound, shown when they are on an alarm project. Disabled when every alarm
 * project enforces its own sound. Test sound plays the chosen one (and arms the audio: it is a click).
 */
function AlarmSoundField({ me, setting, saving, onSave }: { me: Me; setting: MeSetting | undefined; saving: boolean; onSave: (v: AlarmSoundKind | null) => void }): JSX.Element {
  const preference = me.preferences.alarmSound ?? null;
  const allEnforced = setting !== undefined && setting.enforcedBy.length > 0 && setting.enforcedBy.length === setting.projectCount;
  const testKind: AlarmSoundKind = preference ?? 'klaxon';
  return (
    <div className="grid gap-1.5">
      <Label htmlFor="alarm-sound">Alarm sound</Label>
      <div className="flex flex-wrap items-center gap-2">
        <NativeSelect
          id="alarm-sound"
          className="w-48"
          value={preference ?? DEFAULT_OPTION}
          disabled={saving || allEnforced}
          onChange={(e) => {
            const v = ALARM_SOUNDS.find((s) => s === e.target.value) ?? null;
            onSave(v);
          }}
        >
          <option value={DEFAULT_OPTION}>Project default</option>
          {ALARM_SOUNDS.map((s) => (
            <option key={s} value={s}>
              {ALARM_SOUND_LABELS[s]}
            </option>
          ))}
        </NativeSelect>
        <Button variant="outline" size="sm" onClick={() => void playTest(testKind).then((ok) => setSoundBlocked(!ok))}>
          Test sound
        </Button>
      </div>
      {allEnforced ? <p className="text-xs text-muted-foreground">{SET_BY_PROJECT}</p> : <EnforcedNote setting={setting} />}
    </div>
  );
}

/** Original section pattern: the title (with its hint) above the card. */
function SettingsSection({ id, title, hint, children }: { id: string; title: string; hint?: string; children: ReactNode }): JSX.Element {
  return (
    <section aria-labelledby={id} className="mb-8">
      <SectionTitle id={id} title={title} hint={hint} />
      <Card className="space-y-4">{children}</Card>
    </section>
  );
}

export function SettingsPage(): JSX.Element {
  const { data: me } = useMe();
  if (!me)
    return (
      <Page width="2xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  return <Settings me={me} />;
}

function Settings({ me }: { me: Me }): JSX.Element {
  const update = useUpdatePreferences();
  const projects = useProjects();
  const meSettings = useMeSettings();
  const [saving, setSaving] = useState<Field | null>(null);
  const [permission, setPermission] = useState<PermissionState>(() => notificationPermission());
  const [note, setNote] = useState<string | null>(null);

  function save(field: Field, patch: PreferencesPatch): void {
    setSaving(field);
    update.mutate(patch, { onSettled: () => setSaving(null) });
  }

  async function toggleNotifications(on: boolean): Promise<void> {
    setNote(null);
    if (!on) {
      save('notificationsEnabled', { notificationsEnabled: false });
      return;
    }
    let current = notificationPermission();
    if (current === 'default') current = await requestNotificationPermission();
    setPermission(current);
    if (current === 'unsupported') {
      setNote(UNSUPPORTED_NOTE);
      return;
    }
    if (current !== 'granted') {
      setNote(BLOCKED_NOTE);
      return;
    }
    save('notificationsEnabled', { notificationsEnabled: true });
  }

  async function allowHere(): Promise<void> {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'denied') setNote(BLOCKED_NOTE);
  }

  const prefs = me.preferences;
  const defaultProject = prefs.defaultProject;
  const activeProjects = [...(projects.data ?? [])]
    // Sprint 13 (§5.3): a shift project can't be the default for time.
    .filter((p) => p.status === 'active' && !isShiftProject(p))
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
  const browser = browserZone();
  const onButNotHere = prefs.notificationsEnabled && permission !== 'granted';

  return (
    <Page width="2xl">
      <PageHeader title="Settings" hint="Manage your profile and preferences." />

      <SettingsSection id="profile-heading" title="Profile" hint="Your name and email come from your Skaleminds account.">
        <dl className="grid gap-2 text-sm sm:grid-cols-[8rem_1fr]">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="text-foreground">{me.name}</dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd className="break-all text-foreground">{me.email}</dd>
        </dl>
        <p className="text-xs text-muted-foreground">Managed in your Skaleminds account</p>
      </SettingsSection>

      <SettingsSection id="preferences-heading" title="Preferences" hint="Defaults that personalise the app for you.">
        <div className="grid gap-1.5">
          <Label htmlFor="default-project">Default project</Label>
          <NativeSelect
            id="default-project"
            value={defaultProject?.status === 'active' ? defaultProject.id : ''}
            disabled={saving === 'defaultProjectId'}
            onChange={(e) => save('defaultProjectId', { defaultProjectId: e.target.value === '' ? null : e.target.value })}
          >
            <option value="">No default</option>
            {activeProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
          <p className="mt-1.5 text-xs text-muted-foreground">Preselected when starting a timer or adding an entry.</p>
          {defaultProject?.status === 'active' && projects.data && !projects.data.some((p) => p.id === defaultProject.id) && (
            <p className="text-xs text-muted-foreground">{NOT_LOGGABLE_DEFAULT_NOTE}</p>
          )}
          {defaultProject?.status === 'archived' && (
            <p className="text-xs text-muted-foreground">
              {`Your default project ${defaultProject.name} was archived, so new timers and entries start with No project.`}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-foreground">Theme</span>
            <ThemeToggle />
          </div>
          <p className="text-xs text-muted-foreground">Saved in this browser.</p>
        </div>
      </SettingsSection>

      <SettingsSection id="checkins-heading" title="Check-ins" hint="While a timer is running, you'll be periodically asked what you're working on.">
        <div className="grid gap-1.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span id="checkin-enabled-label" className="text-sm text-foreground">
              &quot;Still working?&quot; prompt
            </span>
            <div className="flex items-center gap-2">
              {prefs.checkinEnabledPreference !== null && (
                <Button variant="ghost" size="xs" disabled={saving === 'checkinEnabled'} onClick={() => save('checkinEnabled', { checkinEnabled: null })}>
                  Use default
                </Button>
              )}
              <Switch
                checked={prefs.checkinEnabled}
                aria-labelledby="checkin-enabled-label"
                disabled={saving === 'checkinEnabled'}
                onCheckedChange={(on) => save('checkinEnabled', { checkinEnabled: on })}
              />
            </div>
          </div>
          <EnforcedNote setting={meSettings.data?.find((s) => s.key === 'checkin.enabled')} />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="checkin-interval">Check-in interval</Label>
          <NativeSelect
            id="checkin-interval"
            value={prefs.checkinIntervalPreference === null ? DEFAULT_OPTION : String(prefs.checkinIntervalPreference)}
            disabled={saving === 'checkinIntervalMinutes'}
            onChange={(e) => {
              if (e.target.value === DEFAULT_OPTION) return save('checkinIntervalMinutes', { checkinIntervalMinutes: null });
              const minutes = CHECKIN_INTERVAL_MINUTES.find((m) => String(m) === e.target.value);
              if (minutes !== undefined) save('checkinIntervalMinutes', { checkinIntervalMinutes: minutes });
            }}
          >
            <option value={DEFAULT_OPTION}>
              {prefs.checkinIntervalPreference === null ? `Default (${prefs.checkinIntervalMinutes} min)` : 'Default'}
            </option>
            {CHECKIN_INTERVAL_MINUTES.map((m) => (
              <option key={m} value={String(m)}>
                {intervalLabel(m)}
              </option>
            ))}
          </NativeSelect>
          <p className="mt-1.5 text-xs text-muted-foreground">How often you&apos;re asked &quot;still working on this?&quot; while a timer runs.</p>
          <EnforcedNote setting={meSettings.data?.find((s) => s.key === 'checkin.intervalMinutes')} />
        </div>

        <div className="grid gap-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span id="notifications-label" className="block text-sm text-foreground">
                Browser notifications
              </span>
              <span id="notifications-desc" className="text-xs text-muted-foreground">
                Show a browser notification for check-ins when this tab is in the background
              </span>
            </div>
            {/* A switch, not a "Turn on/Turn off" text button (A-8, UAT-3). */}
            <Switch
              checked={prefs.notificationsEnabled}
              onCheckedChange={(on) => void toggleNotifications(on)}
              aria-labelledby="notifications-label"
              aria-describedby="notifications-desc"
              disabled={saving === 'notificationsEnabled'}
              className="ml-3 mt-0.5"
            />
          </div>
          {note && (
            <p role="status" className="text-xs text-muted-foreground">
              {note}
            </p>
          )}
          {onButNotHere && !note && (
            <div role="status" className="flex flex-wrap items-center gap-2 text-xs text-warning-fg">
              {permission === 'denied' ? (
                <span>{BLOCKED_NOTE}</span>
              ) : permission === 'unsupported' ? (
                <span>{UNSUPPORTED_NOTE}</span>
              ) : (
                <>
                  <span>Notifications are on for your account, but this browser hasn&apos;t allowed them.</span>
                  <Button variant="outline" size="xs" onClick={() => void allowHere()}>
                    Allow in this browser
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        {me.access.alarms === true && <AlarmSoundField me={me} setting={meSettings.data?.find((s) => s.key === 'alarm.sound')} saving={saving === 'alarmSound'} onSave={(v) => save('alarmSound', { alarmSound: v })} />}
      </SettingsSection>

      <SettingsSection id="timezone-heading" title="Timezone">
        <div className="grid gap-1.5">
          <Label id="timezone-label" htmlFor="timezone">
            Timezone
          </Label>
          <TimezonePicker
            id="timezone"
            aria-labelledby="timezone-label timezone-value"
            value={me.timezone}
            disabled={saving === 'timezone'}
            onChange={(tz) => save('timezone', { timezone: tz })}
          />
          {browser && browser !== me.timezone && (
            <div>
              <Button variant="outline" size="xs" disabled={saving === 'timezone'} onClick={() => save('timezone', { timezone: browser })}>
                {`Use this browser's zone (${browser})`}
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{TIMEZONE_HELP}</p>
        </div>
      </SettingsSection>
    </Page>
  );
}
